import type { LessonVideo } from '@/types'
import type { BuildLessonUrlArgs } from '@/utils/buildLessonUrl'
import { parseLessonUrlTimeSeconds } from '@/utils/buildLessonUrl'

export function normalizeRouteQueryString(raw: unknown): string {
  if (raw == null) return ''
  if (Array.isArray(raw)) return String(raw[0] ?? '').trim()
  return String(raw).trim()
}

export function parseNumericCollectionId(raw: unknown): number | undefined {
  const s = normalizeRouteQueryString(raw)
  if (!/^\d+$/.test(s)) return undefined
  return Number(s)
}

export function findLessonVideoByUrlParam(
  sortedVideos: LessonVideo[],
  urlVideoParam: string,
): LessonVideo | null {
  const p = urlVideoParam.trim()
  if (!p || sortedVideos.length === 0) return null
  return (
    sortedVideos.find(
      (v) => (typeof v.slug === 'string' && v.slug === p) || String(v.id) === p,
    ) ?? null
  )
}

export function pickInitialLessonVideo(
  sortedVideos: LessonVideo[],
  urlVideoParam: string,
): LessonVideo | null {
  if (sortedVideos.length === 0) return null
  if (!urlVideoParam) return sortedVideos[0] ?? null
  return findLessonVideoByUrlParam(sortedVideos, urlVideoParam) ?? sortedVideos[0] ?? null
}

export function urlTimeAppliesToVideo(
  urlVideoParam: string,
  video: LessonVideo | null,
  options?: { lessonHasOnlyOneVideo?: boolean },
): boolean {
  if (!video) return false
  if (options?.lessonHasOnlyOneVideo) return true
  const p = urlVideoParam.trim()
  if (!p) return true
  return (
    (typeof video.slug === 'string' && video.slug === p) || String(video.id) === p
  )
}

export function clampSeekSecondsToDuration(
  seconds: number,
  durationSeconds: number | undefined,
): number {
  let clamped = Math.floor(seconds)
  const d = durationSeconds ?? 0
  if (d > 0) clamped = Math.min(Math.max(0, clamped), Math.floor(d))
  else clamped = Math.max(0, clamped)
  return clamped
}

export function deepLinkSeekSecondsForCurrentVideo(input: {
  urlTParam: unknown
  urlVideoParam: string
  currentVideo: LessonVideo | null
  lessonHasOnlyOneVideo?: boolean
}): number | null {
  if (!input.currentVideo) return null
  const t = parseLessonUrlTimeSeconds(input.urlTParam)
  if (t == null) return null
  if (
    !urlTimeAppliesToVideo(input.urlVideoParam, input.currentVideo, {
      lessonHasOnlyOneVideo: input.lessonHasOnlyOneVideo,
    })
  ) {
    return null
  }
  return clampSeekSecondsToDuration(t, input.currentVideo.duration)
}

export function videoQueryValueForShare(input: {
  lessonHasMultipleVideos: boolean
  currentVideo: LessonVideo | null
}): string | undefined {
  if (!input.lessonHasMultipleVideos || !input.currentVideo) return undefined
  const slug = input.currentVideo.slug?.trim()
  if (slug) return slug
  return String(input.currentVideo.id)
}

export function buildLessonShareUrlArgs(input: {
  courseSlug: string
  lessonSlug: string
  collectionId: number | undefined
  referrer: string | undefined
  referrerTitle: string | undefined
  currentTimeSeconds: number
  lessonHasMultipleVideos: boolean
  currentVideo: LessonVideo | null
  shareAtCurrentTime: boolean
  includeLoop: boolean
}): BuildLessonUrlArgs {
  const t =
    input.shareAtCurrentTime && input.currentTimeSeconds > 0
      ? Math.floor(input.currentTimeSeconds)
      : undefined
  return {
    courseSlug: input.courseSlug,
    lessonSlug: input.lessonSlug,
    collectionId: input.collectionId,
    referrer: input.referrer,
    referrerTitle: input.referrerTitle,
    startSeconds: t,
    videoSlug: videoQueryValueForShare({
      lessonHasMultipleVideos: input.lessonHasMultipleVideos,
      currentVideo: input.currentVideo,
    }),
    includeLoop: input.includeLoop || undefined,
  }
}
